import type { Field } from '@skop-app/lib-domain/types'

type FieldErrorLike = {
  type?: string
  message?: unknown
}

export function hasVisibleDependencyValue(value: unknown) {
  if (value === undefined || value === null) return false
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0 && !Number.isNaN(value)
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  return true
}

const isEventLike = (value: unknown): value is { target: any } => {
  return !!value && typeof value === 'object' && 'target' in value
}

const toLocalDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined

  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export const formatDateInputValue = (value: unknown) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return ''

  const year = value.getFullYear()
  const month = `${value.getMonth() + 1}`.padStart(2, '0')
  const day = `${value.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const normalizeFieldChangeValue = (field: Field, incoming: any) => {
  let nextValue = incoming

  if (isEventLike(incoming)) {
    if (field.type === 'checkbox') {
      nextValue = Boolean(incoming.target?.checked)
    } else {
      nextValue = incoming.target?.value
    }
  }

  if (field.type === 'number') {
    if (nextValue === '' || nextValue == null) return undefined
    const parsed = Number(nextValue)
    return Number.isNaN(parsed) ? nextValue : parsed
  }

  if (field.type === 'date') {
    if (nextValue === '' || nextValue == null) return undefined
    if (nextValue instanceof Date) return nextValue
    if (typeof nextValue === 'string') return toLocalDate(nextValue)
  }

  return nextValue
}

export const getValueAtPath = (value: unknown, path: string) => {
  if (!path) return value
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '')
  return normalizedPath
    .split('.')
    .filter(Boolean)
    .reduce((acc: any, segment) => acc?.[segment], value)
}

const startsWithAny = (value: string, prefixes: string[]) =>
  prefixes.some((prefix) => value.startsWith(prefix))

const includesAny = (value: string, patterns: string[]) =>
  patterns.some((pattern) => value.includes(pattern))

const isGenericInvalidTypeMessage = (message?: string) => {
  if (!message) return false
  const normalized = message.trim().toLowerCase()
  return (
    normalized === 'invalid input' ||
    startsWithAny(message, ['Entrée invalide :', 'Invalid input:'])
  )
}

const isGenericTooSmallMessage = (message?: string) => {
  if (!message) return false
  return startsWithAny(message, ['Trop petit :', 'Too small:'])
}

const isGenericTooBigMessage = (message?: string) => {
  if (!message) return false
  return startsWithAny(message, ['Trop grand :', 'Too big:'])
}

const isGenericInvalidFormatMessage = (message?: string) => {
  if (!message) return false
  const normalized = message.trim().toLowerCase()
  return includesAny(normalized, [
    'adresse e-mail invalide',
    'e-mail invalide',
    'email invalide',
    'invalid email',
    'invalid email address',
    'url invalide',
    'invalid url',
  ])
}

const isRequiredLikeTooSmallMessage = (message?: string) => {
  if (!message) return false
  const normalized = message.toLowerCase()
  return />=\s*1(?:\D|$)/.test(normalized) || normalized.includes('at least 1')
}

const getFieldLabelText = (field: Field) =>
  typeof field.label === 'string' && field.label.trim().length > 0
    ? field.label.trim()
    : undefined

const quoteLabel = (label?: string) => (label ? `« ${label} »` : undefined)

export const getFriendlyFieldErrorMessage = (
  field: Field,
  error: FieldErrorLike | undefined | null,
) => {
  if (!error) return undefined

  const rawMessage =
    typeof error.message === 'string' ? error.message.trim() : undefined
  const errorType = error.type
  const normalizedRawMessage = rawMessage?.toLowerCase()
  const hasCustomMessage = Boolean(
    rawMessage &&
      !isGenericInvalidTypeMessage(rawMessage) &&
      !isGenericTooSmallMessage(rawMessage) &&
      !isGenericTooBigMessage(rawMessage) &&
      !isGenericInvalidFormatMessage(rawMessage),
  )

  const label = getFieldLabelText(field)
  const quotedLabel = quoteLabel(label)

  if (errorType === 'invalid_type' && !hasCustomMessage) {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'textarea':
      case 'text-editor':
      case 'time':
      case 'color':
      case 'image-upload':
        return quotedLabel
          ? `Le champ ${quotedLabel} est requis.`
          : 'Ce champ est requis.'
      case 'date':
        return quotedLabel
          ? `Veuillez renseigner une date pour ${quotedLabel}.`
          : 'Veuillez renseigner une date.'
      case 'number':
      case 'stepper':
        return quotedLabel
          ? `Veuillez renseigner un nombre pour ${quotedLabel}.`
          : 'Veuillez renseigner un nombre.'
      case 'select':
      case 'select-async':
      case 'select-create':
        return quotedLabel
          ? `Veuillez sélectionner une valeur pour ${quotedLabel}.`
          : 'Veuillez sélectionner une valeur.'
      case 'checkbox':
        return quotedLabel
          ? `Veuillez cocher ${quotedLabel}.`
          : 'Veuillez cocher cette case.'
      case 'switch':
      case 'fields-array':
        return quotedLabel
          ? `Veuillez renseigner ${quotedLabel}.`
          : 'Veuillez renseigner ce champ.'
      default:
        return quotedLabel
          ? `Le champ ${quotedLabel} est invalide.`
          : 'Valeur invalide.'
    }
  }

  if (
    (errorType === 'too_small' && !hasCustomMessage) ||
    isGenericTooSmallMessage(rawMessage)
  ) {
    const requiredLike = isRequiredLikeTooSmallMessage(rawMessage)

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'textarea':
      case 'text-editor':
      case 'time':
      case 'color':
      case 'image-upload':
        if (requiredLike) {
          return quotedLabel
            ? `Le champ ${quotedLabel} est requis.`
            : 'Ce champ est requis.'
        }
        return quotedLabel
          ? `Le champ ${quotedLabel} est trop court.`
          : 'La valeur saisie est trop courte.'
      case 'number':
      case 'stepper':
        return quotedLabel
          ? `La valeur de ${quotedLabel} est trop petite.`
          : 'La valeur est trop petite.'
      case 'fields-array':
        if (requiredLike) {
          return quotedLabel
            ? `Veuillez ajouter au moins un élément à ${quotedLabel}.`
            : 'Veuillez ajouter au moins un élément.'
        }
        return quotedLabel
          ? `Le nombre d'elements de ${quotedLabel} est insuffisant.`
          : "Le nombre d'elements est insuffisant."
      default:
        return quotedLabel
          ? `La valeur de ${quotedLabel} est trop petite.`
          : 'La valeur est trop petite.'
    }
  }

  if (
    (errorType === 'too_big' && !hasCustomMessage) ||
    isGenericTooBigMessage(rawMessage)
  ) {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'textarea':
      case 'text-editor':
      case 'time':
      case 'color':
      case 'image-upload':
        return quotedLabel
          ? `Le champ ${quotedLabel} est trop long.`
          : 'La valeur saisie est trop longue.'
      case 'number':
      case 'stepper':
        return quotedLabel
          ? `La valeur de ${quotedLabel} est trop grande.`
          : 'La valeur est trop grande.'
      case 'fields-array':
        return quotedLabel
          ? `Le nombre d'elements de ${quotedLabel} est trop eleve.`
          : "Le nombre d'elements est trop eleve."
      default:
        return quotedLabel
          ? `La valeur de ${quotedLabel} est trop grande.`
          : 'La valeur est trop grande.'
    }
  }

  if (
    (errorType === 'invalid_format' ||
      errorType === 'invalid_string' ||
      errorType === 'invalid_date') &&
    (!hasCustomMessage || normalizedRawMessage === 'invalid input')
  ) {
    const normalized = normalizedRawMessage ?? ''

    if (
      field.type === 'email' ||
      normalized.includes('email') ||
      normalized.includes('e-mail')
    ) {
      return quotedLabel
        ? `Veuillez renseigner une adresse e-mail valide pour ${quotedLabel}.`
        : 'Veuillez renseigner une adresse e-mail valide.'
    }

    if (field.type === 'date' || errorType === 'invalid_date') {
      return quotedLabel
        ? `Veuillez renseigner une date valide pour ${quotedLabel}.`
        : 'Veuillez renseigner une date valide.'
    }

    if (normalized.includes('url')) {
      return quotedLabel
        ? `Veuillez renseigner une URL valide pour ${quotedLabel}.`
        : 'Veuillez renseigner une URL valide.'
    }

    return quotedLabel
      ? `Le champ ${quotedLabel} est invalide.`
      : 'Valeur invalide.'
  }

  return rawMessage
}
